'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { pushGtmEvent } from './gtm-data-layer';

/**
 * Last URL an event was emitted for; null until the first emit of this document.
 * Module scope, NOT a ref: it has to live as long as the document, so a remount
 * (changing the `[lang]` segment re-mounts the root layout's children) is still
 * read as a navigation rather than a fresh load. A real reload gives the bundle
 * a fresh module, which resets it — exactly right.
 */
let lastEmittedUrl: string | null = null;

/**
 * `window.location.href` / `usePathname()` come back percent-encoded for any
 * non-ASCII segment (Thai slugs included), which is correct for an actual URL
 * but unreadable once it lands in the dataLayer / GTM debug view. Decode it
 * for reporting only; fall back to the raw value if it's not valid encoding.
 */
function decodeForReporting(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

/**
 * Emits `virtual_page_view` for the initial document load AND for every
 * client-side route change, so one event stream describes every page the user
 * sees. `navigation_type` distinguishes the two.
 *
 * App Router navigations never reload the document, so GTM's Page View trigger
 * cannot see them, and its History Change trigger sees too much — Next calls
 * `pushState` for prefetch and for internal state as well as for real
 * navigations, so that trigger both misses and duplicates. Emitting the event
 * from React is the only reading of "the route changed" that matches what the
 * user actually did.
 *
 * Pair it with a **Custom Event** trigger (`virtual_page_view`) in the GTM
 * container. The container itself comes from the tenant's headerHtml, so that
 * part has to be configured by whoever owns the container, not here. Note the
 * container's own All Pages trigger also fires on the initial load: a tag fed by
 * both would count the landing page twice, which is what `navigation_type` is
 * there to let the container filter on.
 *
 * Renders nothing. Mount it once per document, near the root.
 */
export default function GtmPageView(): null {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const query = searchParams.toString();
    const url = query ? `${pathname}?${query}` : pathname;

    useEffect(() => {
        const previousUrl = lastEmittedUrl;

        // Same URL as the last emit — `searchParams` hands back a new object on
        // unrelated re-renders, and StrictMode runs this effect twice in dev.
        if (previousUrl === url) {
            return;
        }

        lastEmittedUrl = url;

        // previousUrl === null means this is the document's first URL, so the
        // event covers the initial load and there is no in-app page behind it —
        // `document.referrer` (whatever brought the user to the site) is the only
        // honest referrer. On a real navigation the in-app page IS the referrer:
        // `document.referrer` still points at the original external source, so
        // reporting it for every SPA pageview would be wrong.
        const isInitialLoad = previousUrl === null;

        pushGtmEvent({
            event: 'virtual_page_view',
            // 'initial' = the document load, 'spa' = a client-side route change.
            // The container's All Pages trigger already covers the initial load,
            // so a tag that would otherwise double-count the landing page can be
            // narrowed to navigation_type equals 'spa' from the GTM side alone.
            navigation_type: isInitialLoad ? 'initial' : 'spa',
            page_location: decodeForReporting(window.location.href),
            page_path: decodeForReporting(pathname),
            page_query: decodeForReporting(query),
            page_title: document.title,
            page_referrer: isInitialLoad
                ? document.referrer
                : decodeForReporting(`${window.location.origin}${previousUrl}`),
        });
    }, [url, pathname, query]);

    return null;
}
